import React from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  CreditCard, CheckCircle, Star, Shield, Lightning,
  Handshake, ShieldCheck, Headset, Receipt, FilePdf,
} from '@phosphor-icons/react';
import { Loader2 } from 'lucide-react';

const PLAN_STYLES = {
  opc: { accent: '#0055FF', ideal: 'Idéal pour OPC indépendant', btn: 'bg-[#0055FF] hover:bg-blue-700 text-white', border: 'border-blue-200 hover:border-blue-400 hover:shadow-md', check: 'border-blue-200 text-blue-300 bg-blue-50/50' },
  csps: { accent: '#FF6B00', ideal: 'Idéal pour coordonnateur SPS', btn: 'bg-[#FF6B00] hover:bg-orange-700 text-white', border: 'border-orange-200 hover:border-orange-400 hover:shadow-md', check: 'border-orange-200 text-orange-300 bg-orange-50/50' },
  opc_csps: { accent: '#10B981', ideal: 'Solution complète chantier', btn: 'bg-emerald-600 hover:bg-emerald-700 text-white shadow-lg shadow-emerald-200', border: '', check: 'border-emerald-200 text-emerald-300 bg-emerald-50/50' },
};

function PlanCard({ plan, isAdmin, checkingOut, onCheckout }) {
  const isPopular = plan.popular;
  const profileType = plan.profile_type;
  const style = PLAN_STYLES[profileType] || PLAN_STYLES.opc_csps;
  const isEvolutif = profileType === 'opc' || profileType === 'csps';

  return (
    <Card className={`relative border-2 transition-all flex flex-col ${
      isPopular ? 'border-emerald-500 shadow-2xl shadow-emerald-100 ring-2 ring-emerald-400/30 scale-[1.02] md:scale-[1.06] z-10' : style.border
    }`} data-testid={`plan-card-${plan.id}`}>
      {isPopular && (
        <div className="absolute -top-4 left-1/2 -translate-x-1/2 z-10">
          <Badge className="bg-emerald-500 text-white px-5 py-1.5 text-xs font-bold whitespace-nowrap shadow-lg" data-testid="popular-badge">
            <Star size={13} weight="fill" className="mr-1.5" /> Le plus utilisé
          </Badge>
        </div>
      )}
      <CardContent className={`p-5 sm:p-6 flex flex-col flex-1 ${isPopular ? 'pt-9' : 'pt-5'}`}>
        <div>
          <h3 className={`font-bold text-[#111827] ${isPopular ? 'text-xl' : 'text-lg'}`}>{plan.name}</h3>
          <p className="text-xs font-semibold mt-1" style={{ color: style.accent }} data-testid={`ideal-for-${plan.id}`}>{style.ideal}</p>
        </div>
        <div className="mt-3 flex items-baseline gap-1">
          <span className={`font-black text-[#111827] ${isPopular ? 'text-4xl sm:text-5xl' : 'text-3xl sm:text-4xl'}`}>{plan.price}</span>
          <span className="text-sm text-[#6B7280]">€</span>
          <span className="text-xs text-[#9CA3AF] ml-1">/ mois</span>
        </div>
        {profileType === 'opc_csps' && (
          <p className="text-xs text-emerald-600 font-semibold mt-1" data-testid="savings-line">Économisez 39 €/mois vs. OPC + CSPS séparés</p>
        )}
        <p className="text-xs text-[#6B7280] mt-2">{plan.description}</p>
        <ul className="mt-4 space-y-2 flex-1">
          {(plan.features || []).map((f, i) => (
            <li key={`plan-feat-${f.slice(0,15)}-${i}`} className="flex items-start gap-2 text-sm text-[#374151]">
              <CheckCircle size={16} style={{ color: style.accent }} className="flex-shrink-0 mt-0.5" weight="fill" />
              {f}
            </li>
          ))}
        </ul>
        <div className="mt-5 space-y-2">
          {isAdmin ? (
            <div className={`w-full h-12 rounded-md border-2 border-dashed flex items-center justify-center text-xs font-medium ${style.check}`} data-testid={`admin-preview-${plan.id}`}>
              Aperçu — bouton visible pour les utilisateurs
            </div>
          ) : (
            <Button onClick={() => onCheckout(plan.id)} disabled={!!checkingOut}
              className={`w-full h-12 text-sm font-bold ${style.btn}`} data-testid={`checkout-${plan.id}`}>
              {checkingOut === plan.id ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <CreditCard size={18} className="mr-2" />}
              Commencer mon essai gratuit
            </Button>
          )}
          <p className="text-[10px] text-[#6B7280] text-center font-medium">Sans engagement — Résiliation en 1 clic</p>
          <p className="text-[10px] text-[#9CA3AF] text-center">7 jours gratuits puis {plan.price} €/mois</p>
          {isEvolutif && (
            <p className="text-[11px] text-emerald-600 font-medium text-center" data-testid={`evolutif-${plan.id}`}>Évolutif vers OPC + CSPS</p>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

export function PlansSection({ plans, isAdmin, checkingOut, onCheckout }) {
  return (
    <>
      <div className="text-center pt-2" data-testid="plans-headline">
        <h2 className="text-xl sm:text-2xl font-bold text-[#111827]">Choisissez la formule adaptée à votre métier</h2>
        <p className="text-sm text-[#4B5563] mt-1.5 max-w-lg mx-auto">Sans engagement — Mise en place en 2 minutes</p>
      </div>
      <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2" data-testid="reassurance-strip">
        <span className="flex items-center gap-1.5 text-xs font-medium text-[#374151]"><Handshake size={16} className="text-[#FF6B00]" weight="fill" /> Sans engagement</span>
        <span className="flex items-center gap-1.5 text-xs font-medium text-[#374151]"><ShieldCheck size={16} className="text-[#FF6B00]" weight="fill" /> Résiliable à tout moment</span>
        <span className="flex items-center gap-1.5 text-xs font-medium text-[#374151]"><Headset size={16} className="text-[#FF6B00]" weight="fill" /> Support inclus</span>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6 items-stretch" data-testid="plans-grid">
        {plans.map(plan => (
          <PlanCard key={plan.id} plan={plan} isAdmin={isAdmin} checkingOut={checkingOut} onCheckout={onCheckout} />
        ))}
      </div>
      <div className="text-center py-4" data-testid="cta-footer">
        <div className="inline-flex items-center gap-2 bg-[#111827] text-white px-6 py-3 rounded-full text-sm font-bold shadow-lg">
          <Star size={16} weight="fill" className="text-[#FF6B00]" /> Commencer mon essai gratuit — Sans engagement
        </div>
        <p className="text-xs text-[#6B7280] mt-3 max-w-md mx-auto" data-testid="checkout-legal-mention">
          En souscrivant, vous acceptez nos{' '}
          <a href="/cgv" target="_blank" rel="noopener noreferrer" className="text-[#0055FF] hover:underline">CGV</a>
          {' et notre '}
          <a href="/confidentialite" target="_blank" rel="noopener noreferrer" className="text-[#0055FF] hover:underline">politique de confidentialité</a>.
          {' '}Paiement sécurisé par Stripe.
        </p>
      </div>
    </>
  );
}

export function BenefitsCard() {
  return (
    <Card className="bg-gray-50 border-gray-200">
      <CardContent className="p-4 sm:p-6">
        <h3 className="font-bold text-sm text-[#111827] mb-3 flex items-center gap-2">
          <Shield size={18} className="text-[#FF6B00]" weight="fill" /> Inclus dans tous les abonnements
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {['Mises à jour incluses', 'Support par email', 'Export PDF & Word', 'Documents illimités'].map((item, i) => (
            <div key={`benefit-${item.slice(0,15)}-${i}`} className="flex items-center gap-2 text-xs text-[#4B5563]">
              <Lightning size={14} className="text-[#FF6B00]" weight="fill" /> {item}
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

export function InvoicesList({ invoices, downloadInvoice }) {
  if (!invoices.length) return null;
  return (
    <div data-testid="invoices-section">
      <h3 className="font-bold text-base text-[#111827] mb-3 flex items-center gap-2">
        <Receipt size={20} className="text-[#FF6B00]" /> Factures
      </h3>
      <div className="space-y-2">
        {invoices.map(inv => (
          <Card key={inv.id} className="border" data-testid={`invoice-${inv.id}`}>
            <CardContent className="p-3 sm:p-4 flex items-center justify-between gap-3">
              <div className="flex items-center gap-3 min-w-0">
                <Receipt size={20} className="text-gray-400 flex-shrink-0" />
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-[#111827] truncate">{inv.invoice_number}</p>
                  <p className="text-xs text-[#6B7280]">{inv.plan_name} — {inv.amount} EUR — {new Date(inv.created_at).toLocaleDateString('fr-FR')}</p>
                </div>
              </div>
              <Button variant="outline" size="sm" onClick={() => downloadInvoice(inv.id, inv.invoice_number)}
                className="h-9 text-[#FF6B00] flex-shrink-0" data-testid={`download-invoice-${inv.id}`}>
                <FilePdf size={16} className="mr-1" /> PDF
              </Button>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
